import React, { useEffect, useRef, useState } from "react";
import {
  CommentOutlined,
  CloseOutlined,
  MinusOutlined,
  SendOutlined,
  HistoryOutlined,
  PlusOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { Popconfirm, Spin, Empty } from "antd";
import {
  sendChat,
  getChatConversations,
  getChatConversation,
  deleteChatConversation,
} from "../../../services/features/kapInsights.service";
import { resolveSiteVisitStatusName } from "../../../services/Enums/siteVisitStatusEnum";
import { formatDate } from "../../../services/helpFunctions/formatDateAndTime";
import { RolesEnum } from "../../../services/rolesEnum";
import { isAuthorized } from "../../../services/authService";
import Preview from "../../pages/planning/VisitBoard/TableView/modals/Preview";
import "./ProjectChatbot.scss";

// Always-on chat assistant, pinned bottom-right inside a project. Powered by the
// persisted conversational chat API (multi-turn memory + saved history), the
// same data-grounded search used in the AI Assistant → Ask tab.
const SUGGESTIONS = [
  "Visits with missing tracking last month",
  "Flags opened this week",
  "Site visits in review",
  "Tasks overdue",
];

const WELCOME = {
  role: "bot",
  text: "Hi! Ask me anything about this project — visits, flags, tasks, IPs. I'll search and pull the records for you.",
  showSuggestions: true,
};

const isSiteVisitItem = (item, entity) =>
  (item?.type || entity || "").toLowerCase().includes("sitevisit");

// Server message → local render shape. Server roles are "user"/"assistant".
const toLocalMessage = (m) => {
  const d = m?.data || {};
  return {
    role: m?.role === "user" ? "user" : "bot",
    text: m?.content || d.interpretation || "",
    results: Array.isArray(d.results) ? d.results : undefined,
    entity: d.entity,
    count: d.count,
  };
};

const ProjectChatbot = () => {
  const projectID = localStorage.getItem("currentProjectID");
  const isGuestShare = process.env.REACT_APP_API_IS_GUEST;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([WELCOME]);

  // Conversation history drawer.
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const loadConversations = () => {
    if (!projectID) return;
    setLoadingHistory(true);
    getChatConversations(
      projectID,
      (res) => {
        const list = res?.data || res || [];
        setConversations(Array.isArray(list) ? list : []);
        setLoadingHistory(false);
      },
      () => setLoadingHistory(false)
    );
  };

  const openHistory = () => {
    setShowHistory(true);
    loadConversations();
  };

  const startNewChat = () => {
    setConversationId(null);
    setMessages([WELCOME]);
    setShowHistory(false);
  };

  const openConversation = (id) => {
    if (!id) return;
    setShowHistory(false);
    setLoadingConversation(true);
    getChatConversation(
      id,
      (res) => {
        const data = res?.data || res || {};
        const msgs = (data.messages || []).map(toLocalMessage);
        setConversationId(data.id || id);
        setMessages(msgs.length ? msgs : [WELCOME]);
        setLoadingConversation(false);
      },
      () => {
        setLoadingConversation(false);
      }
    );
  };

  const handleDeleteConversation = (id) => {
    deleteChatConversation(
      id,
      () => {
        setConversations((list) => list.filter((c) => c.id !== id));
        if (id === conversationId) startNewChat();
      },
      () => {}
    );
  };

  const send = (text) => {
    const question = (text ?? input).trim();
    if (!question || loading) return;
    const isNew = !conversationId;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setLoading(true);
    sendChat(
      { conversationId, projectId: projectID, message: question },
      (res) => {
        const payload = res?.data || res || {};
        const inner = payload.data || {};
        if (payload.conversationId) setConversationId(payload.conversationId);
        setMessages((m) => [
          ...m,
          {
            role: "bot",
            text:
              payload.answer ||
              inner.interpretation ||
              `Here's what I found${
                inner.count != null ? ` (${inner.count})` : ""
              }.`,
            results: Array.isArray(inner.results) ? inner.results : [],
            entity: inner.entity,
            count: inner.count,
          },
        ]);
        setLoading(false);
        // First reply in a brand-new conversation — refresh the saved list so
        // it shows up (with the server-generated title) next time history opens.
        if (isNew) loadConversations();
      },
      () => {
        setMessages((m) => [
          ...m,
          {
            role: "bot",
            text: "Sorry, I couldn't run that just now. Please try rephrasing.",
          },
        ]);
        setLoading(false);
      }
    );
  };

  // Restricted to System Admin and Project Manager, and only inside a project
  // (needs a current project to scope the search).
  const canUseChat = isAuthorized([
    RolesEnum.SystemAdmin,
    RolesEnum.ProjectManager,
  ]);
  if (!projectID || isGuestShare || !canUseChat) return null;

  return (
    <>
      {!open && (
        <button
          className="aiChatLauncher"
          onClick={() => setOpen(true)}
          aria-label="Open project assistant"
          title="Ask the project assistant"
        >
          <CommentOutlined />
          <span className="aiChatLauncherDot" />
        </button>
      )}

      {open && (
        <div className="aiChatPanel" role="dialog" aria-label="Project assistant">
          <div className="aiChatHeader">
            <div className="aiChatTitle">
              <span className="aiChatAvatar">
                <CommentOutlined />
              </span>
              <div>
                Ask the project
                <small>AI search · answers from your data</small>
              </div>
            </div>
            <div className="aiChatHeaderBtns">
              <button
                className="aiChatIconBtn"
                onClick={startNewChat}
                aria-label="New chat"
                title="New chat"
              >
                <PlusOutlined />
              </button>
              <button
                className={`aiChatIconBtn ${showHistory ? "active" : ""}`}
                onClick={() => (showHistory ? setShowHistory(false) : openHistory())}
                aria-label="Conversation history"
                title="Conversation history"
              >
                <HistoryOutlined />
              </button>
              <button
                className="aiChatIconBtn"
                onClick={() => setOpen(false)}
                aria-label="Minimise"
                title="Minimise"
              >
                <MinusOutlined />
              </button>
              <button
                className="aiChatIconBtn"
                onClick={() => setOpen(false)}
                aria-label="Close"
                title="Close"
              >
                <CloseOutlined />
              </button>
            </div>
          </div>

          {showHistory ? (
            <div className="aiChatHistory">
              <div className="aiChatHistoryHead">
                <button
                  className="aiChatHistoryBack"
                  onClick={() => setShowHistory(false)}
                >
                  <ArrowLeftOutlined /> Back to chat
                </button>
                <button className="aiChatHistoryNew" onClick={startNewChat}>
                  <PlusOutlined /> New
                </button>
              </div>
              <div className="aiChatHistoryList">
                {loadingHistory ? (
                  <div className="aiChatHistoryLoading">
                    <Spin size="small" />
                  </div>
                ) : conversations.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No past conversations yet"
                  />
                ) : (
                  conversations.map((c) => (
                    <div
                      key={c.id}
                      className={`aiChatHistoryItem ${
                        c.id === conversationId ? "current" : ""
                      }`}
                      onClick={() => openConversation(c.id)}
                    >
                      <div className="aiChatHistoryItemMain">
                        <span className="aiChatHistoryTitle">
                          {c.title || "Untitled conversation"}
                        </span>
                        {(c.updatedDate || c.createdDate) && (
                          <span className="aiChatHistoryDate">
                            {formatDate(c.updatedDate || c.createdDate)}
                          </span>
                        )}
                      </div>
                      <Popconfirm
                        title="Delete this conversation?"
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                        onConfirm={(e) => {
                          e?.stopPropagation?.();
                          handleDeleteConversation(c.id);
                        }}
                        onCancel={(e) => e?.stopPropagation?.()}
                      >
                        <button
                          className="aiChatHistoryDelete"
                          aria-label="Delete conversation"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DeleteOutlined />
                        </button>
                      </Popconfirm>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="aiChatBody" ref={bodyRef}>
                {loadingConversation ? (
                  <div className="aiChatHistoryLoading">
                    <Spin size="small" />
                  </div>
                ) : (
                  messages.map((m, idx) => (
                    <React.Fragment key={idx}>
                      <div className={`aiChatMsg ${m.role}`}>
                        {m.role === "bot" && m.entity != null && (
                          <div className="aiChatMeta">
                            {m.entity}
                            {m.count != null ? ` · ${m.count} result(s)` : ""}
                          </div>
                        )}
                        <span>{m.text}</span>

                        {Array.isArray(m.results) && m.results.length > 0 && (
                          <ul className="aiChatResults">
                            {m.results.slice(0, 12).map((item, i) => {
                              const canPreview =
                                isSiteVisitItem(item, m.entity) && item.id;
                              const statusText =
                                canPreview && item.status
                                  ? resolveSiteVisitStatusName(item.status).text
                                  : item.status;
                              return (
                                <li
                                  key={i}
                                  className={canPreview ? "clickable" : ""}
                                  onClick={
                                    canPreview
                                      ? () => setPreviewId(item.id)
                                      : undefined
                                  }
                                >
                                  <span className="aiChatResultTitle">
                                    {item.title || item.id}
                                  </span>
                                  {(item.type || statusText) && (
                                    <span className="aiChatResultMeta">
                                      {[item.type, statusText]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        {m.showSuggestions && (
                          <div className="aiChatSuggestions">
                            {SUGGESTIONS.map((s) => (
                              <button key={s} onClick={() => send(s)}>
                                {s}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  ))
                )}

                {loading && (
                  <div className="aiChatTyping">
                    <span />
                    <span />
                    <span />
                  </div>
                )}
              </div>

              <div className="aiChatFooter">
                <input
                  value={input}
                  placeholder="Ask a question…"
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") send();
                  }}
                />
                <button
                  className="aiChatSend"
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  aria-label="Send"
                >
                  <SendOutlined />
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <Preview
        visible={!!previewId}
        siteVisitCardId={previewId}
        onClose={() => setPreviewId(null)}
      />
    </>
  );
};

export default ProjectChatbot;
